import {
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { exportToExcel, getTemplateColumns } from "../../../utils/util";
import { DownloadDoneOutlined } from "@mui/icons-material";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import InfoIcon from "@mui/icons-material/Info";
import AddToTargetBtn from "./AddToTargetBtn";

const RdCampaignImport = ({ campaignPost, setCampaignPost, setActiveStep }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (campaignPost.contacts.length > 0 && excelData.length === 0) {
      setExcelData(campaignPost.contacts);
    }
  }, [campaignPost.contacts, excelData.length]);
  const handleTemplateDownload = () => {
    if (!campaignPost.template) return;
    const columnHeaders = campaignPost?.isStaticParams
      ? ["Numbers"]
      : ["Numbers"].concat(getTemplateColumns(campaignPost.template));
    exportToExcel(
      [columnHeaders],
      `template_${campaignPost.template.name}.xlsx`,
      true
    );
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setExcelLoading(false);
      return;
    }
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      toast.warn("Please upload a valid Excel file");
      setExcelLoading(false);
      return;
    }

    // Validate file size
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (file.size > MAX_FILE_SIZE) {
      toast.warn("File size exceeds 50MB limit");
      setExcelLoading(false);
      return;
    }

    setExcelLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, {
          type: "array",
          cellDates: true, // Parse dates as Date objects
          cellText: false, // Don't use formatted text
          cellNF: true, // Include number format
        });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: false, // KEY CHANGE: Use formatted text values instead of raw numbers
        });

        if (jsonData.length === 0) {
          toast.warn("The uploaded file is empty");
          setExcelData([]);
          return;
        }

        const headers = jsonData[0];
        if (!headers || headers.length === 0) {
          toast.warn("No headers found in the file");
          setExcelData([]);
          return;
        }

        // Check if "Numbers" column exists
        const numbersColumnIndex = headers.findIndex(
          (header) => header.toLowerCase() === "numbers"
        );

        if (numbersColumnIndex === -1) {
          toast.warn("Required 'Numbers' column not found in the file");
          setExcelData([]);
          return;
        }

        // Filter out rows with empty Numbers field and format the data
        const formattedData = jsonData.slice(1).reduce((acc, row, rowIndex) => {
          // Skip if Numbers field is empty
          if (
            !row[numbersColumnIndex] ||
            row[numbersColumnIndex].toString().trim() === ""
          ) {
            return acc;
          }

          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] ?? "";
          });
          acc.push(obj);
          return acc;
        }, []);

        // Optional: Show warning if any rows were skipped
        const skippedRows = jsonData.length - 1 - formattedData.length;
        if (skippedRows > 0) {
          toast.warn(`Skipped ${skippedRows} rows with empty Numbers field`);
        }

        setExcelData(formattedData);
      } catch (err) {
        toast.error("Error reading file");
        console.error("File read error:", err);
      } finally {
        setExcelLoading(false);
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
      setExcelLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };
  function generateTargetAudience() {
    if (excelData.length < 1) {
      toast.error("Please import contacts first!");
      return;
    }
    const paramsLength = Object.values(excelData[0]).length - 1;
    const isStaticParams = Boolean(campaignPost?.isStaticParams);
    if (isStaticParams && paramsLength > 0) {
      toast.error("Static params require only the numbers list.");
      return;
    }
    // if (
    //   !isStaticParams &&
    //   requiredParamsLength > 0 &&
    //   requiredParamsLength !== paramsLength
    // ) {
    //   toast.error(`This campaign requires ${requiredParamsLength} parameters.`);
    //   return;
    // }
    setCampaignPost((prev) => ({ ...prev, contacts: excelData }));
    toast.success("Contacts list added to Target!");
    setActiveStep(2);
  }
  return (
    <Box>
      <Stack direction="row" alignItems="center" mb={2}>
        <Typography variant="body2">
          Please read these instructions before importing
        </Typography>
        <IconButton onClick={handlePopoverOpen} size="small" sx={{ ml: 1 }}>
          <InfoIcon />
        </IconButton>
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            border: "1px solid",
            borderColor: "grey.200",
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            fontWeight="600"
            mb={1}
          >
            File Requirements
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0, "& li": { mb: 1 } }}>
            <Typography component="li" variant="body2">
              The first column must be the whatsapp numbers
            </Typography>
            <Typography component="li" variant="body2">
              After first column, your variable names(numbered eg. {`{{1}}`} or
              named eg. {`{{'order_id'}}`}) must appear in the same order in
              which they appear in the message body.
            </Typography>
            <Typography component="li" variant="body2">
              Atleast one line item should be there
            </Typography>
          </Box>
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button size="small" onClick={handlePopoverClose}>
              Got it
            </Button>
          </Box>
        </Box>
      </Popover>
      <Stack direction={"row"} spacing={1}>
        <Button
          startIcon={<ImportExportIcon />}
          variant="contained"
          component="label"
          disabled={excelLoading}
          sx={{ mb: 3 }}
        >
          {excelLoading ? "Processing..." : "Import Excel File"}
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            hidden
          />
        </Button>
        <Button
          onClick={handleTemplateDownload}
          startIcon={<DownloadDoneOutlined />}
          variant="outlined"
        >
          Download template
        </Button>
      </Stack>
      {excelData.length > 10 && (
        <Typography variant="caption">First 10 items in the sheet</Typography>
      )}
      {excelData.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {Object.keys(excelData[0]).map((header) => (
                  <TableCell
                    key={header}
                    sx={{ bgcolor: "grey.100", fontWeight: "bold" }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {excelData.slice(0, 10).map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Object.keys(excelData[0]).map((header) => (
                    <TableCell key={`${rowIndex}-${header}`}>
                      {row[header]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <AddToTargetBtn onClick={generateTargetAudience} />
    </Box>
  );
};

export default RdCampaignImport;
